import React, { useRef, useState, useMemo } from 'react';
import AddressSearch from 'pages/Authentication/components/AddressSearch';
import { EProfessions } from 'types/Entitites';
import {
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  LinearProgress,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Address, ResultItem } from 'pages/Authentication/types/AddressSearchAutoComplete';
import { useMutation, useQuery } from 'react-query';
import { ToastProps, ToastType } from 'components/Common/Toast';
import { associateFreeTrialPractice, fetchPracticesDropdown, sendFreeTrialOtp } from 'pages/Dashboard/services/freeTrial.service';
import http from 'utils/http';
import { AssociateFreeTrialPracticePayload } from 'pages/Dashboard/types/freeTrial.types';
import Theme from 'theme';
import { trackMixpanelEvent, logSentryError } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import { useFreeTrialOnboarding } from 'pages/FreeTrialActivation/components/FreeTrialOnboardingContext';
import RadarIcon from '@mui/icons-material/Radar';
import { useLocation } from 'react-router-dom';

export interface OrganizationInfoInitialValues {
  practice: {
    name: string;
    address: {
      street: string;
      state: string;
      zip: string;
      city: string;
      streetAdditional: string;
      fieldValue: {
        id: string;
        label: string;
      };
      country: string;
    };
  };
  npiNumber: number;
  designation: string;
  estimatedPatientPopulation: string;
}
interface OrganizationInfoProps {
  onNext?: () => void;
  step?: number;
  totalSteps?: number;
  providerId?: string;
  setToastProps: (props: ToastProps | null) => void;
  initialValues?: OrganizationInfoInitialValues;
}

const patientOptions = [
  '1-5',
  '6-10',
  '11-15',
  '16-20',
  '21-25',
  '26+',
];

// Calculate goal based on patient range string (e.g., '16-20' => 20*16)
function calculateGoal(patientsRange: string): number {
  if (!patientsRange) return 200;
  // Match patterns like '16-20', '26+', etc.
  const match = /([0-9]+)[^0-9]*([0-9]+|\+)?/.exec(patientsRange);
  let max = 0;
  if (match) {
    if (match[2] === '+') {
      max = parseInt(match[1], 10);
    }
    else if (match[2]) {
      max = parseInt(match[2], 10);
    }
    else {
      max = parseInt(match[1], 10);
    }
  }
  if (patientsRange.includes('+')) max = 26;
  const goal = max * 16;
  return goal || 200;
}

export default function OrganizationInfo({
  onNext,
  step,
  totalSteps = 6,
  providerId,
  setToastProps,
  initialValues,
}: OrganizationInfoProps) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search || '');
  const trialDays = searchParams.get('freetrialdays') || '';
  const trialCode = searchParams.get('freetrialcode') || '';
  const referralCode = searchParams.get('freetrialreferralcode') || '';
  const eventProps = useMemo(() => ({
    trial_days: trialDays,
    trial_code: trialCode,
    referral_code: referralCode,
  }), [trialDays, trialCode, referralCode]);
  React.useEffect(() => {
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_ORG_INFO_DIALOG_VIEWED, eventProps);
  }, [eventProps]);

  const onboarding = useFreeTrialOnboarding();
  const { orgInfo, setOrgInfo } = onboarding;
  const { form, practiceSearch } = orgInfo;
  const setForm = React.useCallback(
    (updater: ((f: typeof form) => typeof form)
      | typeof form) => setOrgInfo((prev) => ({
      ...prev,
      form:
          typeof updater === 'function'
            ? (updater as (f: typeof form) => typeof form)(prev.form)
            : updater,
    })),
    [setOrgInfo],
  );

  // Set form from initialValues on mount if provided and form is empty
  React.useEffect(() => {
    if (
      initialValues
      && initialValues.practice
      && (!form.practiceName && !form.designation && !form.npi && !form.patients)
    ) {
      const addr = initialValues.practice.address || {};
      const fieldValue = addr.street ? { label: addr.street, id: addr.fieldValue?.id || '' } : undefined;
      setForm({
        practiceName: initialValues.practice.name || '',
        address: { ...addr, fieldValue },
        designation: initialValues.designation || '',
        npi: initialValues.npiNumber ? String(initialValues.npiNumber) : '',
        patients: initialValues.estimatedPatientPopulation || '',
      });
    }
  }, [initialValues, form.designation, form.npi, form.patients, form.practiceName, setForm]);

  const designationEnum = useMemo(
    () => EProfessions[form.designation as keyof typeof EProfessions],
    [form.designation],
  );

  const shouldShowNpiField = useMemo(
    () => !!form.designation && ![
      EProfessions.PROVIDER_DESIGNATION_BILLING_ADMIN,
      EProfessions.PROVIDER_DESIGNATION_OFFICE_STAFF,
      EProfessions.PROVIDER_DESIGNATION_OTHER,
    ].includes(designationEnum),
    [form.designation, designationEnum],
  );

  const setPracticeSearch = (val: string | ((prev: string) => string)) => setOrgInfo((prev) => ({ ...prev, practiceSearch: typeof val === 'function' ? val(prev.practiceSearch) : val }));

  const {
    data: practicesDropdown,
    isLoading: practicesLoading,
    refetch: refetchPracticesDropdown,
  } = useQuery({
    queryKey: ['free-trial-practices-dropdown', practiceSearch],
    queryFn: () => fetchPracticesDropdown(http.get, practiceSearch),
    enabled: !!practiceSearch,
  });

  const sendOtpMutation = useMutation({
    mutationFn: (otpProviderId: string) => sendFreeTrialOtp(http.post, otpProviderId),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_SEND_OTP_ERROR, eventProps);
      logSentryError(`Error sending OTP - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Failed to send verification code. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_CREATE_ACCOUNT_SEND_OTP_SUCCESS, eventProps);
      if (onNext) onNext();
    },
  });

  const mutation = useMutation<unknown, unknown, AssociateFreeTrialPracticePayload>({
    mutationFn: (payload) => associateFreeTrialPractice(http.post, payload),
    onError: (error) => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_ORG_INFO_ERROR, eventProps);
      logSentryError(`Error saving organization info - ${(error as Error)?.message}`);
      setToastProps({
        message: 'Failed to save organization info. Please try again.',
        open: true,
        type: ToastType.error,
      });
    },
    onSuccess: () => {
      trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_ORG_INFO_SUCCESS, eventProps);
      setToastProps({
        message: 'Organization info saved!',
        open: true,
        type: ToastType.success,
      });
      // After org info is saved, send OTP
      if (providerId) {
        sendOtpMutation.mutate(providerId);
      }
    },
  });

  const onAddressSelect = React.useCallback(
    (address: Address | null, value: ResultItem | null) => {
      if (!address) {
        return;
      }

      setForm({
        ...form,
        address: {
          street: form.address?.street,
          state: address.state?.longName,
          zip: address.zipCode?.longName,
          city: address.city?.longName,
          streetAdditional: '',
          fieldValue: value,
          country: address.country ?? 'USA',
        },
      });
    },
    [form, setForm],
  );

  const formRef = useRef<HTMLFormElement | null>(null);
  const [patientsError, setPatientsError] = useState('');
  const [addressError, setAddressError] = useState('');

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    // Trim all necessary fields
    const trimmedPracticeName = form.practiceName?.trim() || '';
    const trimmedDesignation = form.designation?.trim() || '';
    const trimmedNpi = form.npi?.trim() || '';
    const trimmedPatients = form.patients?.trim() || '';
    const trimmedDesignationDesc = form.designationDesc?.trim() || '';
    const trimmedAddress = {
      street: form.address.street?.trim() || '',
      streetAdditional: form.address.streetAdditional?.trim() || '',
      city: form.address.city?.trim() || '',
      state: form.address.state?.trim() || '',
      country: form.address.country?.trim() || 'USA',
      zip: form.address.zip?.trim() || '',
    };
    // Address validation
    if (!trimmedAddress.street) {
      setAddressError('Practice address is required.');
      return;
    }
    else {
      setAddressError('');
    }
    if (shouldShowNpiField) {
      if (!trimmedPatients) {
        setPatientsError('Please select the number of patients you see per day.');
        return;
      }
      else {
        setPatientsError('');
      }
    }
    trackMixpanelEvent(MixpanelEventName.FREE_TRIAL_ORG_INFO_SUBMIT_BUTTON_CLICKED, {
      practice: trimmedPracticeName,
      address: JSON.stringify(trimmedAddress),
      designation: trimmedDesignation,
      npi: trimmedNpi,
      patients: trimmedPatients,
      ...eventProps,
    });
    if (formRef.current?.reportValidity()) {
      if (!providerId) {
        setToastProps({
          message: 'Provider ID missing.',
          open: true,
          type: ToastType.error,
        });
        return;
      }
      mutation.mutate({
        providerId,
        practice: {
          name: trimmedPracticeName,
          address: trimmedAddress,
        },
        npiNumber: Number(trimmedNpi),
        designation: trimmedDesignation,
        notes: '',
        designationDesc: trimmedDesignationDesc,
        estimatedPatientPopulation: trimmedPatients,
      });
    }
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 400, fontSize: 34, pb: 0, pl: 3, pt: 2 }}>
        About your practice
      </DialogTitle>
      <LinearProgress
        variant='determinate'
        value={(step as number / totalSteps as number) * 100}
        color='success'
        sx={{
          width: '100%',
          borderRadius: '24px 24px 0 0',
          height: 4,
          my: 1,
        }}
      />
      <DialogContent sx={{ pt: 1 }}>
        <form ref={formRef} onSubmit={handleSubmit}>
          <Typography fontSize={16} sx={{ mb: 3 }}>
            Tell us a bit about your organization so we can
            tailor your demo experience with Headlamp.
          </Typography>
          <Autocomplete
            freeSolo
            loading={practicesLoading}
            options={practicesDropdown?.practices?.map((practice) => practice.name) || []}
            value={form.practiceName}
            onInputChange={(_, newValue) => {
              setForm((f) => ({ ...f, practiceName: newValue }));
              setPracticeSearch(newValue);
              refetchPracticesDropdown();
            }}
            onChange={(_, value) => {
              const selectedPractice = practicesDropdown?.practices?.find(
                (p) => p.name === value,
              );
              setForm((f) => {
                if (selectedPractice?.address) {
                  return {
                    ...f,
                    practiceName: value || '',
                    address: {
                      ...f.address,
                      street: selectedPractice.address.street,
                      streetAdditional: selectedPractice.address.streetAdditional,
                      city: selectedPractice.address.city,
                      state: selectedPractice.address.state,
                      zip: selectedPractice.address.zip,
                      country: selectedPractice.address.country,
                      fieldValue: { label: selectedPractice.address.street, id: selectedPractice.address.fieldValue?.id || '' },
                    },
                  };
                }
                else {
                  return {
                    ...f,
                    practiceName: value || '',
                    address: {},
                  };
                }
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Practice name'
                variant='outlined'
                sx={{ flex: 1, mb: 3, width: '75%' }}
                required
              />
            )}
          />
          <Box sx={{ width: '75%' }}>
            <AddressSearch
              inputLabel='Practice address'
              addressValue={form.address.fieldValue || { label: '', id: 'defaultId' }}
              onAddressSelect={onAddressSelect}
              onInputChange={(value) => {
                setForm({
                  ...form,
                  address: {
                    ...form.address,
                    street: value,
                    fieldValue: { label: value },
                  },
                });
                if (value) setAddressError('');
              }}
            />
            {addressError && (
              <Typography color='error' sx={{ fontSize: 13, mt: 0.5 }}>
                {addressError}
              </Typography>
            )}
          </Box>
          {form.address.fieldValue && form.address.fieldValue.label && (
            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
              <TextField
                label='Ste, bldg, apt, etc.'
                value={form.address.streetAdditional || ''}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  address: { ...f.address, streetAdditional: e.target.value, country: 'USA' },
                }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label='City'
                value={form.address.city || ''}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  address: { ...f.address, city: e.target.value, country: 'USA' },
                }))}
                sx={{ flex: 1 }}
                required
              />
              <TextField
                label='State'
                value={form.address.state || ''}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  address: { ...f.address, state: e.target.value, country: 'USA' },
                }))}
                sx={{ flex: 1 }}
                required
              />
              <TextField
                label='Zip'
                value={form.address.zip || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^[0-9\b]+$/.test(val)) {
                    setForm((f) => ({
                      ...f,
                      address: { ...f.address, zip: val, country: 'USA' },
                    }));
                  }
                }}
                inputProps={{ maxLength: 5, minLength: 5 }}
                sx={{ flex: 1 }}
                required
              />
            </div>
          )}
          <TextField
            label='Professional Designation'
            variant='outlined'
            select
            value={form.designation}
            onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
            sx={{ flex: 1, mb: 3, width: '50%', mr: 30, mt: 3 }}
            required
          >
            {Object.entries(EProfessions)
              .filter(([key]) => key !== 'PROVIDER_DESIGNATION_BILLING_ADMIN')
              .map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
          </TextField>
          {form.designation === 'PROVIDER_DESIGNATION_OTHER' && (
            <TextField
              label='Please specify your physician specialty'
              variant='outlined'
              value={form.designationDesc || ''}
              onChange={(e) => setForm((f) => ({ ...f, designationDesc: e.target.value }))}
              sx={{ flex: 1, mb: 3, width: '50%', mr: 30 }}
              required
            />
          )}
          {shouldShowNpiField && (
            <>
              <TextField
                label='NPI number'
                variant='outlined'
                type='text'
                value={form.npi}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow only digits, max 10 characters
                  if (/^\d{0,10}$/.test(val)) {
                    setForm((f) => ({ ...f, npi: val }));
                  }
                }}
                sx={{ flex: 1, mb: 3 }}
                required
                inputProps={{
                  maxLength: 10,
                  inputMode: 'numeric',
                  pattern: '\\d{10}',
                }}
              />
              <Typography sx={{ fontSize: 16, mb: 1.5 }}>
                How many patients do you see
                {' '}
                <Box component='span' fontWeight={700} display='inline' fontSize={16}>per day</Box>
                {' '}
                (on average)?
              </Typography>
              <ToggleButtonGroup
                value={form.patients}
                exclusive
                onChange={(_, val) => {
                  setForm((f) => ({ ...f, patients: val }));
                  if (val) setPatientsError('');
                }}
                sx={{
                  mb: 1.5,
                  width: '100%',
                  '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                    marginLeft: '-1px',
                    borderLeft: '1px solid #33739D',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  },
                }}
              >
                {patientOptions.map((option) => (
                  <ToggleButton
                    key={option}
                    value={option}
                    sx={{
                      flex: 1,
                      border: '1px solid #33739D',
                      borderRadius: 0,
                      fontWeight: 500,
                      fontSize: 14,
                      color: Theme.custom.colors.lightPrimaryMain,
                      '&.Mui-selected': {
                        background: '#E3EDF7',
                        color: Theme.custom.colors.lightPrimaryMain,
                      },
                    }}
                  >
                    {option}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {patientsError && (
                <Typography color='error' sx={{ fontSize: 13, mt: 0.5, mb: 1 }}>
                  {patientsError}
                </Typography>
              )}
              {form.patients && (
                <Box sx={{
                  background: '#E6F4EA',
                  color: Theme.custom.colors.lightTextPrimary,
                  borderRadius: 1,
                  px: 2,
                  py: 1,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                }}
                >
                  <RadarIcon sx={{ mr: 1, color: Theme.custom.colors.lightSuccessMain }} fontSize='small' />
                  <Typography sx={{ fontSize: 16 }}>
                    Based on this, we&apos;ll set an
                    {' '}
                    <b>
                      initial goal
                    </b>
                    {' '}
                    of adding
                    {' '}
                    {calculateGoal(form.patients)}
                    {' '}
                    patients to Headlamp. You can edit this goal.
                  </Typography>
                </Box>
              )}
            </>
          )}
          <Button
            variant='contained'
            sx={{
              fontSize: 15,
              borderRadius: 8,
              boxShadow: 'none',
              py: 1.5,
              px: 6,
              background: Theme.custom.colors.lightPrimaryMain,
              color: Theme.custom.colors.white,
            }}
            type='submit'
            onClick={handleSubmit}
            disabled={mutation.isLoading || sendOtpMutation.isLoading}
          >
            CUSTOMIZE MY EXPERIENCE
          </Button>
        </form>
      </DialogContent>
    </>
  );
}
